import { useEffect, useId, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { creerClient } from '../../api/client.js';
import type { Ecran } from '../../api/contrat.js';
import { Lien, useNaviguer } from '../../app/navigation.js';
import { POLICE_EQUIPE } from '../../app/styles.js';
import { COULEURS as UI, ESPACEMENTS } from '../../../../../packages/ui/src/tokens/tokens.js';
import { CATALOGUE_DEMARRAGE } from '../demarrage/catalogue.js';
import { resumer, type AvancementEtape } from '../demarrage/modele.js';
import { adresseDuGuide } from './CadreGuide.js';

/**
 * D18 « Mode guidé » (couloir G, point 1) : le poste de pilotage simple d'Isabelle et de Brigitte. Le nombre de dossiers en cours,
 * la liste courte avec l'étape en cours de chacun et « Reprendre », et un grand bouton « Créer un nouveau dossier » qui ouvre
 * directement le démarrage guidé du dossier créé. Visuel du prototype de référence : carte indicateur à gros chiffre coloré,
 * sections en cartes arrondies (jetons de `packages/ui`).
 */
interface DossierGuide {
  dossierId: string;
  reference: string;
  titre: string;
  etapes: AvancementEtape[];
}
type Lecture = { statut: 'chargement' } | { statut: 'pret'; dossiers: DossierGuide[] } | { statut: 'erreur' };

const carte: CSSProperties = {
  background: UI.fond,
  border: '1px solid #D9DBE7',
  borderRadius: 16,
  padding: ESPACEMENTS.l,
  boxShadow: '0 1px 3px rgba(31, 35, 71, 0.08)',
  boxSizing: 'border-box',
};
const grosBouton: CSSProperties = {
  minHeight: 56,
  padding: '0 28px',
  borderRadius: 999,
  border: 0,
  background: UI.bouton,
  color: '#FFFFFF',
  font: 'inherit',
  fontSize: 18,
  fontWeight: 700,
  cursor: 'pointer',
};
const boutonReprendre: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 48,
  padding: '0 22px',
  borderRadius: 999,
  background: UI.marine,
  color: '#FFFFFF',
  fontWeight: 600,
  textDecoration: 'none',
};

const champ: CSSProperties = { minHeight: 48, font: 'inherit', padding: '0 12px', borderRadius: 12, border: `1px solid ${UI.bordure}` };

const dossiersEnCours = (n: number): string => `${n > 1 ? 'dossiers' : 'dossier'} en cours`;

function etapeDe(d: DossierGuide): string {
  const r = resumer(CATALOGUE_DEMARRAGE, d.etapes);
  if (r.finDeMission) return `Mission terminée à l’étape ${r.finDeMission.etape.numero} : l’avocat partenaire prend le relais`;
  if (!r.courante) return 'Toutes les étapes du parcours sont faites';
  return `Étape ${r.courante.numero} sur ${CATALOGUE_DEMARRAGE.etapes.length} : ${r.courante.titre}`;
}

export function EcranModeGuide({ ecran }: { ecran: Ecran }) {
  const naviguer = useNaviguer();
  const client = useMemo(() => creerClient({ fetch: (...a) => globalThis.fetch(...a) }), []);
  const [lecture, setLecture] = useState<Lecture>({ statut: 'chargement' });
  const [creation, setCreation] = useState(false);
  const [nom, setNom] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const idNom = useId();
  const [prenomDefunt, setPrenomDefunt] = useState('');
  const [nomDefunt, setNomDefunt] = useState('');
  const idPrenomDefunt = useId();
  const idNomDefunt = useId();

  useEffect(() => {
    let annule = false;
    client.appeler<{ dossiers: DossierGuide[] }>('demarrage.tableau').then(
      (t) => !annule && setLecture({ statut: 'pret', dossiers: t.dossiers }),
      () => !annule && setLecture({ statut: 'erreur' }),
    );
    return () => {
      annule = true;
    };
  }, [client]);

  const creer = async (e?: FormEvent) => {
    e?.preventDefault();
    if (nom.trim() === '' || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      const d = await client.appeler<{ id: string }>('dossiers.creer', { corps: { title: nom.trim(), mandantPersonId: null, etudeId: null } });
      // Tournage du 30/10, défaut 2 : le défunt (rôle « auteur ») est créé tout de suite s'il est saisi, par la route de la généalogie.
      // Son échec ne retient pas l'ouverture du guide : l'étape 8 propose de le saisir à nouveau (jamais bloqué).
      if (nomDefunt.trim() !== '' || prenomDefunt.trim() !== '')
        await client
          .appeler('genealogie.personnes.creer', {
            parametres: { dossierId: d.id },
            corps: { firstNames: prenomDefunt.trim(), lastName: nomDefunt.trim() || prenomDefunt.trim(), vitalStatus: 'decede', isHypothesis: false, role: 'auteur', deathDate: null },
          })
          .catch(() => undefined);
      naviguer(adresseDuGuide(d.id));
    } catch {
      setErreur('Le dossier n’a pas été créé. Vérifiez la connexion puis réessayez.');
    } finally {
      setEnCours(false);
    }
  };

  const dossiers = lecture.statut === 'pret' ? lecture.dossiers : [];
  return (
    <section
      data-ecran={ecran.id}
      style={{ fontFamily: POLICE_EQUIPE, fontSize: 18, lineHeight: 1.5, color: UI.texte, display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.l, maxWidth: 960 }}
    >
      <h1 style={{ margin: 0, color: UI.marine, fontSize: 30 }}>{ecran.titre}</h1>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: ESPACEMENTS.m, alignItems: 'stretch' }}>
        <div data-indicateur="en-cours" style={{ ...carte, flex: '1 1 220px' }}>
          <p style={{ margin: 0, color: UI.texteSecondaire, fontSize: 16 }}>Vos dossiers</p>
          <p style={{ margin: 0 }}>
            <span style={{ display: 'block', fontSize: 48, fontWeight: 700, color: UI.bouton, lineHeight: 1.1 }}>
              {lecture.statut === 'pret' ? dossiers.length : '…'}
            </span>{' '}
            <span>{dossiersEnCours(dossiers.length)}</span>
          </p>
        </div>
        <div style={{ ...carte, flex: '2 1 320px', display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center' }}>
          {creation ? (
            <form onSubmit={(e) => void creer(e)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label htmlFor={idNom} style={{ fontWeight: 600 }}>
                Nom du nouveau dossier (par exemple « Succession Exemple »)
              </label>
              <input
                id={idNom}
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                style={champ}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label htmlFor={idPrenomDefunt} style={{ fontWeight: 600 }}>
                    Prénom du défunt <span style={{ fontWeight: 400, color: UI.texteSecondaire }}>(facultatif)</span>
                  </label>
                  <input id={idPrenomDefunt} value={prenomDefunt} onChange={(e) => setPrenomDefunt(e.target.value)} style={champ} />
                </div>
                <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label htmlFor={idNomDefunt} style={{ fontWeight: 600 }}>
                    Nom du défunt <span style={{ fontWeight: 400, color: UI.texteSecondaire }}>(facultatif)</span>
                  </label>
                  <input id={idNomDefunt} value={nomDefunt} onChange={(e) => setNomDefunt(e.target.value)} style={champ} />
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <button type="button" style={grosBouton} disabled={nom.trim() === '' || enCours} onClick={() => void creer()}>
                  Créer et commencer
                </button>
                <button
                  type="button"
                  style={{ ...grosBouton, background: UI.fond, color: UI.marine, border: `2px solid ${UI.marine}` }}
                  onClick={() => setCreation(false)}
                >
                  Annuler
                </button>
              </div>
              {erreur ? (
                <p role="alert" style={{ margin: 0, color: UI.erreur }}>
                  {erreur}
                </p>
              ) : null}
            </form>
          ) : (
            <>
              <button type="button" style={grosBouton} onClick={() => setCreation(true)}>
                Créer un nouveau dossier
              </button>
              <p style={{ margin: 0, color: UI.texteSecondaire, fontSize: 16 }}>Le démarrage guidé du dossier s’ouvre aussitôt : l’outil vous pose les questions étape par étape.</p>
            </>
          )}
        </div>
      </div>
      <section aria-labelledby="guide-dossiers" style={{ ...carte, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 id="guide-dossiers" style={{ margin: 0, color: UI.marine, fontSize: 22 }}>
          Reprendre un dossier
        </h2>
        {lecture.statut === 'chargement' ? <p role="status" style={{ margin: 0 }}>Chargement de vos dossiers…</p> : null}
        {lecture.statut === 'erreur' ? (
          <p role="alert" style={{ margin: 0 }}>
            Vos dossiers n’ont pas pu être chargés. Vérifiez la connexion puis rechargez la page.
          </p>
        ) : null}
        {lecture.statut === 'pret' && dossiers.length === 0 ? <p style={{ margin: 0 }}>Aucun dossier en cours : créez-en un.</p> : null}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {dossiers.map((d) => (
            <li
              key={d.dossierId}
              data-dossier-guide={d.dossierId}
              style={{ border: '1px solid #D9DBE7', borderRadius: 14, padding: ESPACEMENTS.m, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', background: UI.fondDoux }}
            >
              <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 700, color: UI.marine }}>{d.titre}</span>
                <span style={{ fontSize: 16, color: UI.texteSecondaire }}>{d.reference}</span>
                <span style={{ fontSize: 16 }}>{etapeDe(d)}</span>
              </div>
              <Lien vers={adresseDuGuide(d.dossierId)} style={boutonReprendre} enfants="Reprendre" />
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
